/**
 * Client Order Detail Page
 * View individual order details and cancel if needed
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../../api/client';
import Badge from '../../../components/ui/Badge';
import type { OrderWithItems } from '@phoenixbill/shared/types/order';

export default function ClientOrderDetailPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderWithItems | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);

  useEffect(() => {
    if (orderId) {
      loadOrder();
    }
  }, [orderId]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/client/orders/${orderId}`);
      setOrder(response.data.data);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) {
      setError('Please provide a cancellation reason');
      return;
    }

    try {
      setCancelling(true);
      await api.post(`/client/orders/${orderId}/cancel`, {
        reason: cancelReason,
      });
      await loadOrder();
      setShowCancelDialog(false);
      setCancelReason('');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to cancel order');
    } finally {
      setCancelling(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount);
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      pending: { variant: 'warning', label: 'Pending' },
      active: { variant: 'success', label: 'Active' },
      fraud: { variant: 'danger', label: 'Under Review' },
      cancelled: { variant: 'default', label: 'Cancelled' },
    };
    const config = statusMap[status] || statusMap.pending;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  const getPaymentBadge = (status: string) => {
    const statusMap: Record<string, { variant: 'success' | 'warning' | 'danger' | 'default'; label: string }> = {
      unpaid: { variant: 'warning', label: 'Unpaid' },
      paid: { variant: 'success', label: 'Paid' },
      refunded: { variant: 'default', label: 'Refunded' },
    };
    const config = statusMap[status] || statusMap.unpaid;
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-8 text-center">
          <p className="text-slate-400">Order not found</p>
          <button
            onClick={() => navigate('/client/orders')}
            className="mt-4 px-6 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
          >
            Back to Orders
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/client/orders')}
          className="text-orange-400 hover:text-orange-300 mb-4 inline-flex items-center"
        >
          ← Back to Orders
        </button>
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-100">Order #{order.orderNumber}</h1>
            <p className="text-slate-400 mt-1">
              Placed on {new Date(order.createdAt).toLocaleDateString()} at{' '}
              {new Date(order.createdAt).toLocaleTimeString()}
            </p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(order.status)}
            {getPaymentBadge(order.paymentStatus)}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Status Messages */}
      {order.status === 'pending' && (
        <div className="mb-6 bg-orange-900/20 border border-orange-800 rounded-lg p-4">
          <p className="text-orange-400 text-sm">
            Your order is pending payment. Once payment is received, we'll activate your services automatically.
          </p>
        </div>
      )}

      {order.status === 'fraud' && (
        <div className="mb-6 bg-yellow-900/20 border border-yellow-800 rounded-lg p-4">
          <p className="text-yellow-400 text-sm">
            Your order is currently under review for security purposes. Our team will review it shortly. You'll receive an email once approved.
          </p>
        </div>
      )}

      {/* Order Items */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">Order Items</h2>
        <div className="space-y-3">
          {order.items.map((item) => (
            <div key={item.id} className="bg-slate-900 rounded-lg p-4">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <h3 className="font-medium text-gray-100">{item.productName}</h3>
                  <p className="text-sm text-slate-400">
                    {item.billingCycle} × {item.quantity}
                  </p>
                  {item.domain && (
                    <p className="text-sm text-gray-500">Domain: {item.domain}</p>
                  )}
                  {item.configurableOptions && Object.keys(item.configurableOptions).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-slate-700/50">
                      <p className="text-xs text-gray-500 mb-1">Configuration:</p>
                      {Object.entries(item.configurableOptions).map(([key, value]) => (
                        <p key={key} className="text-xs text-slate-400">
                          {key}: {String(value)}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  <p className="font-semibold text-gray-100">{formatPrice(item.total)}</p>
                  {item.setupFee > 0 && (
                    <p className="text-xs text-slate-400">+{formatPrice(item.setupFee)} setup</p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Order Summary */}
        <div className="mt-6 pt-6 border-t border-slate-700/50">
          <div className="space-y-2 text-sm">
            <div className="flex justify-between text-slate-300">
              <span>Subtotal</span>
              <span>{formatPrice(order.subtotal)}</span>
            </div>
            {order.setupFeeTotal > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>Setup Fees</span>
                <span>{formatPrice(order.setupFeeTotal)}</span>
              </div>
            )}
            {order.discount > 0 && (
              <div className="flex justify-between text-green-400">
                <span>
                  Discount
                  {order.promoCode && <span className="ml-1">({order.promoCode})</span>}
                </span>
                <span>-{formatPrice(order.discount)}</span>
              </div>
            )}
            {order.taxAmount > 0 && (
              <div className="flex justify-between text-slate-300">
                <span>Tax ({(order.taxRate * 100).toFixed(1)}%)</span>
                <span>{formatPrice(order.taxAmount)}</span>
              </div>
            )}
            <div className="border-t border-slate-700/50 pt-2">
              <div className="flex justify-between text-lg font-semibold text-gray-100">
                <span>Total</span>
                <span>{formatPrice(order.total)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-100 mb-4">Billing Address</h2>
        <div className="text-slate-300 text-sm space-y-1">
          <p>
            {order.firstName} {order.lastName}
          </p>
          {order.companyName && <p>{order.companyName}</p>}
          <p>{order.address1}</p>
          {order.address2 && <p>{order.address2}</p>}
          <p>
            {order.city}, {order.state} {order.postcode}
          </p>
          <p>{order.country}</p>
          {order.phoneNumber && <p>Phone: {order.phoneNumber}</p>}
        </div>
      </div>

      {/* Order Notes */}
      {order.notes && (
        <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-100 mb-4">Order Notes</h2>
          <p className="text-slate-300 text-sm whitespace-pre-wrap">{order.notes}</p>
        </div>
      )}

      {/* Actions */}
      {order.status !== 'cancelled' && (
        <div className="flex gap-4">
          <button
            onClick={() => setShowCancelDialog(true)}
            className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            Cancel Order
          </button>
        </div>
      )}

      {/* Cancel Dialog */}
      {showCancelDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800/80 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 max-w-md w-full mx-4">
            <h3 className="text-xl font-semibold text-gray-100 mb-4">Cancel Order</h3>
            <p className="text-slate-400 text-sm mb-4">
              Are you sure you want to cancel this order? Please provide a reason.
            </p>
            <textarea
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 mb-4 bg-slate-900 border border-slate-700/50 rounded-lg text-gray-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              placeholder="Reason for cancellation..."
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                }}
                className="flex-1 px-4 py-2 bg-slate-700/50 text-gray-200 rounded-lg hover:bg-slate-600/50 transition-colors"
              >
                Close
              </button>
              <button
                onClick={handleCancelOrder}
                disabled={cancelling || !cancelReason.trim()}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {cancelling ? 'Cancelling...' : 'Cancel Order'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
